/**
 * BetaGate — route-level guard for billing + integrations pages.
 *
 * When the relevant feature flag is OFF (i.e. the user is not on
 * `REKTRA_BETA_ALLOWLIST`), this component renders nothing and redirects
 * to `/dashboard`. We don't 404 the route on the frontend — that would
 * leak the path's existence to the URL bar — instead we silently redirect
 * to the dashboard, where the beta banner reminds the user why nothing
 * blew up.
 *
 * The matching backend middleware also 404s these endpoints for the same
 * users, so a beta user typing `/api/integrations/payday/status` into
 * Postman gets a 404 with no body / no detail leak.
 *
 * Use as a wrapper in App.js:
 *   <Route path="/settings/billing"
 *     element={<BetaGate need="billing"><BillingSettings /></BetaGate>} />
 */
import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

export const BetaGate = ({ need, children }) => {
  const { billingEnabled, integrationsEnabled, loading } = useAuth();
  const location = useLocation();
  // Wait for /auth/me to land before deciding — don't flash redirect on
  // the very first render. ProtectedRoute already handles unauth.
  if (loading) return null;
  const ok =
    (need === 'billing' && billingEnabled) ||
    (need === 'integrations' && integrationsEnabled);
  if (!ok) {
    return <Navigate to="/dashboard" replace state={{ from: location }} />;
  }
  return <>{children}</>;
};

export default BetaGate;
